import { useLocation, useNavigate } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TextField } from '@mui/material';
import { IContactInfo } from '../../interface/contact';
import '../styles/page.css';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import Loader from 'components/Loader';
import AOS from 'aos';
import 'aos/dist/aos.css';

function Contact() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const serviceNm = searchParams.get('service');
  const contactType =
    searchParams.get('no') === '01' ||
    searchParams.get('no') === null ||
    searchParams.get('no') === undefined
      ? '서비스도입문의'
      : '제품소개서신청';

  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const inputRef3 = useRef<HTMLInputElement>(null);
  const inputRef4 = useRef<HTMLInputElement>(null);
  const inputRef5 = useRef<HTMLInputElement>(null);

  const [isTermOpen, setIsTermOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  /** Form Validataion */
  const schema = z.object({
    serviceNm: z.string(),
    contactType: z.string(),
    compNm: z
      .string()
      .trim()
      .min(1, { message: '고객사명을 입력해 주세요.' })
      .max(100, { message: '최대 글자수(100자)를 초과하였습니다.' }),
    userMobile: z
      .string()
      .trim()
      .min(1, { message: '휴대폰번호를 입력해 주세요.' })
      .max(11, { message: '최대 글자수(11자)를 초과하였습니다.' })
      .regex(
        RegExp(/^(01[0]{1})[0-9]{4}[0-9]{4}$/),
        '올바른 형식의 휴대폰번호가 아닙니다( - 기호 없음)',
      ),
    userEmail: z
      .string()
      .trim()
      .min(1, { message: '이메일을 입력해 주세요.' })
      .max(100, { message: '최대 글자수(100자)를 초과하였습니다.' })
      .email({ message: '올바른 형식의 이메일이 아닙니다.' }),
    title: z
      .string()
      .trim()
      .min(1, { message: '문의제목을 입력해 주세요.' })
      .max(100, { message: '최대 글자수(100자)를 초과하였습니다.' }),
    contents: z.string().trim(),
    agreeYn: z.string(),
    remarks: z.string(),
  });

  /** Hook */
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      serviceNm: '',
      contactType: '',
      compNm: '',
      userMobile: '',
      userEmail: '',
      title: '',
      contents: '',
      agreeYn: '',
      remarks: '',
    },
  });

  const { control, handleSubmit, getValues, setValue } = form;

  // 제품선택
  const [checkedList, setCheckedList] = useState<Array<string>>([]);
  const handleServiceCheckBoxChange = (checked: boolean, item: string) => {
    if (checked && item) {
      if (item === 'none') {
        setCheckedList([]);
        setCheckedList(['none']); // 제품 미선택
      } else {
        if (checkedList[0] === 'none') {
          setCheckedList([]);
          setCheckedList((prev) => [...prev, item]);
        } else setCheckedList((prev) => [...prev, item]);
      }
    } else if (!checked) {
      setCheckedList(checkedList.filter((el) => el !== item));
    }
  };

  function isChecked(serviceNm: string): boolean {
    if (checkedList) return checkedList.indexOf(serviceNm) !== -1;
    return false;
  }

  // 저장 및 이메일 발송
  const onSubmit = async () => {
    if (checkedList.length <= 0) {
      alert('문의할 제품을 선택해주세요.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (getValues('agreeYn') === '' || getValues('agreeYn') === 'N') {
      alert('개인정보 수집 및 이용동의 약관에 동의해주세요.');
      return;
    }

    const result = confirm('문의내용을 전송하시겠습니까?');
    if (!result) return;

    const serviceToString = checkedList.join(',');
    const formData: IContactInfo = {
      serviceNm: serviceToString, // 제품선택
      contactType: getValues('contactType'), // 문의유형
      compNm: getValues('compNm'), //고객사명
      userMobile: getValues('userMobile'), // 연락처
      userEmail: getValues('userEmail'), // 이메일
      title: getValues('title'), // 문의제목
      contents: getValues('contents').replace(/(?:\r\n|\r|\n)/g, '<br>'), // 문의내용
      agreeYn: getValues('agreeYn'), // 동의여부
      remarks: '',
    };

    setLoading(true);
    await fetch(`/contact/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(formData),
    })
      .then((res) => {
        if (res.status === 200) return res.json();
        else return null;
      })
      .then((res) => {
        if (res !== null) {
          navigate(0);
          alert('문의하신 내용이 전송되었습니다.\n담당자로부터 빠른 시일내에 답변드릴 예정입니다.');
        } else {
          alert('문의하기 전송에 실패하였습니다.');
        }
        setLoading(false);
      });
  };

  const handleAgreeCheckBoxChange = (checked: boolean, value: string) => {
    if (checked) setValue('agreeYn', 'Y');
    else setValue('agreeYn', 'N');
  };

  const handleTermOpen = () => {
    switch (isTermOpen) {
      case true:
        setIsTermOpen(false);
        break;

      case false:
        setIsTermOpen(true);
        break;

      default:
        break;
    }
  };

  const handleBlur = (targetValue: string | number, targetName: string) => {
    // 엔터키로 포커스 이동 시 공백이면 reset
    const valLength = Number(targetValue.toString().trim().length);

    if (valLength === 0) {
      switch (targetName) {
        case 'compNm':
          if (valLength === 0) setValue('compNm', '');
          break;

        case 'userMobile':
          if (valLength === 0) setValue('userMobile', '');
          break;

        case 'userEmail':
          if (valLength === 0) setValue('userEmail', '');
          break;

        case 'title':
          if (valLength === 0) setValue('title', '');
          break;

        case 'contents':
          if (valLength === 0) setValue('contents', '');
          break;

        default:
          break;
      }
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLDivElement> & React.ChangeEvent<HTMLInputElement>,
  ) => {
    const name = e.target.name;
    const len = e.target.value.length;
    const charCode = e.key;

    const isNumber = /[0-9]/.test(charCode);

    if (charCode === 'Enter') {
      e.preventDefault();
      if (e.target === inputRef1.current) inputRef2.current?.focus();
      else if (e.target === inputRef2.current) inputRef3.current?.focus();
      else if (e.target === inputRef3.current) inputRef4.current?.focus();
      else if (e.target === inputRef4.current) inputRef5.current?.focus();
    }

    switch (name) {
      case 'userMobile':
        if (len >= 11 && isNumber) {
          e.preventDefault();
        }
        break;
    }
  };

  const getOnlyNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '');
  };

  // 문의유형 세팅
  const selectType = (e: any) => {
    setValue('contactType', e.target.value);
  };

  useEffect(() => {
    const firstErrorField = Object.keys(form.formState.errors)[0];

    if (firstErrorField) {
      const field = document.querySelector(`[name="${firstErrorField}"]`);

      if (field) {
        (field as HTMLElement).focus();
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [form.formState.errors]);

  useEffect(() => {
    AOS.init();

    if (serviceNm !== null && contactType !== null) {
      handleServiceCheckBoxChange(true, serviceNm);
      setValue('contactType', contactType);
    } else {
      handleServiceCheckBoxChange(true, 'uxbooster');
      setValue('contactType', '서비스도입문의');
    }

    inputRef1.current?.focus();
  }, []);

  return (
    <>
      {loading ? <Loader /> : null}
      <FormProvider {...form}>
        <section id="contact-banner">
          <div className="page-banner-bg">
            <div className="m-auto page-banner-container">
              <h2 data-aos="fade-right" data-aos-duration="700" data-aos-once="true">
                제품문의
              </h2>
              <p
                className="color-caption fs-base"
                data-aos="fade-right"
                data-aos-duration="700"
                data-aos-once="true"
                data-aos-delay="200"
              >
                문의를 남겨주시면 담당자가 빠른 시일내에 회신 드릴 예정입니다.
              </p>
            </div>
          </div>
        </section>

        <main id="question">
          <div className="question-container m-auto">
            <div className="question-content">
              <div className="question-item">
                <p className="fs-md fw-bold">제품선택</p>
              </div>
              <div className="question-product">
                <ul>
                  <li>
                    <input
                      type="checkbox"
                      id="none"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('none')}
                    />
                    <label htmlFor="none">
                      <p className="fs-base fw-bold">제품 미선택</p>
                      <p className="mt-8 fs-xs color-caption">
                        제품군 상관없이 테이크에게 프로젝트를 문의합니다.
                      </p>
                    </label>
                  </li>
                </ul>

                <div className="product-divider"></div>
                <ul>
                  <li>
                    <input
                      type="checkbox"
                      id="uxbooster"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('uxbooster')}
                    />
                    <label htmlFor="uxbooster">
                      <img className="img-24" src="./images/logo-uxbooster.svg" alt="" />
                      <p className="mt-12 fs-xs color-caption">
                        업무 시스템을 효율적으로 구축하는 웹 기반 프레임워크
                      </p>
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="uxboard"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('uxboard')}
                    />
                    <label htmlFor="uxboard">
                      <img className="img-24" src="./images/logo-uxboard.svg" alt="" />
                      <p className="mt-12 fs-xs color-caption">
                        핵심 정보를 한눈에 파악할 수 있는 대쉬보드 구축 서비스
                      </p>
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="uxcreator"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('uxcreator')}
                    />
                    <label htmlFor="uxcreator">
                      <img className="img-24" src="./images/logo-uxcreator.svg" alt="" />
                      <p className="mt-12 fs-xs color-caption">
                        업무화면 · 소스코드를 자동 생성해주는 플랫폼
                      </p>
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="aichanger"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('aichanger')}
                    />
                    <label htmlFor="aichanger">
                      <img className="img-28" src="./images/logo-aichanger.svg" alt="" />
                      <p className="mt-8 fs-xs color-caption">
                        기존 C/S 시스템을 웹 표준 기반으로 전환해주는 서비스
                      </p>
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="gems"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('gems')}
                    />
                    <label htmlFor="gems">
                      <img className="img-28" src="./images/logo-gems.svg" alt="" />
                      <p className="mt-8 fs-xs color-caption">
                        2050 탄소 중립 실현을 위한 필수 솔루션
                      </p>
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="epro"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('epro')}
                    />
                    <label htmlFor="epro">
                      <img className="img-28" src="./images/logo-epro.svg" alt="" />
                      <p className="mt-8 fs-xs color-caption">
                        전자구매 업무 프로세스의 시스템화를 위한 EPRO
                      </p>
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="si"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('si')}
                    />
                    <label htmlFor="si">
                      <p className="fs-base fw-bold color-primary">SI 서비스 및 컨설팅</p>
                      <p className="mt-8 fs-xs color-caption">
                        고객의 비즈니스 혁신을 실현하는 맞춤형 시스템 통합 서비스
                      </p>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
            <div className="question-line"></div>
            <div className="question-content">
              <div className="question-item">
                <p className="fs-md fw-bold">정보입력</p>
              </div>
              <div className="question-write">
                <ul>
                  <li>
                    <label className="fs-base essential">문의 유형</label>
                    <div className="radio-box">
                      <div className="radio-btn">
                        <input
                          className="radio-input"
                          type="radio"
                          id="option01"
                          value="서비스도입문의"
                          name="question-reason"
                          defaultChecked={
                            '서비스도입문의' === contactType
                              ? true
                              : null === contactType
                                ? true
                                : false
                          }
                          onClick={selectType}
                        />
                        <label htmlFor="option01">서비스 도입 문의</label>
                      </div>
                      <div className="radio-btn">
                        <input
                          className="radio-input"
                          type="radio"
                          id="option02"
                          value="제품소개서신청"
                          name="question-reason"
                          defaultChecked={'제품소개서신청' === contactType ? true : false}
                          onClick={selectType}
                        />
                        <label htmlFor="option02">제품소개서 신청</label>
                      </div>
                      <div className="radio-btn">
                        <input
                          className="radio-input"
                          type="radio"
                          id="option03"
                          value="기타"
                          name="question-reason"
                          onClick={selectType}
                        />
                        <label htmlFor="option03">기타(ex.교육신청)</label>
                      </div>
                    </div>
                  </li>
                  <li>
                    <label className="fs-base essential">고객사</label>
                    <div>
                      <Controller
                        name="compNm"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef1}
                            placeholder="고객사명을 입력해주세요."
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                  <li>
                    <label className="fs-base essential">연락처</label>
                    <div>
                      <Controller
                        name="userMobile"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef2}
                            type="tel"
                            placeholder="휴대폰번호를 입력하세요. (예. 01012345678)"
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onInput={getOnlyNumber}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                  <li>
                    <label className="fs-base essential">이메일</label>
                    <div>
                      <Controller
                        name="userEmail"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef3}
                            maxRows={2}
                            placeholder="이메일 주소를 입력하세요."
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                  <li>
                    <label className="fs-base essential">문의제목</label>
                    <div>
                      <Controller
                        name="title"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef4}
                            maxRows={2}
                            placeholder="제목을 입력하세요."
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                  <li>
                    <label className="fs-base">문의내용</label>
                    <div>
                      <Controller
                        name="contents"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef5}
                            placeholder="문의내용을 입력하세요."
                            multiline
                            minRows={10}
                            maxRows={10}
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                </ul>
                <div className="privacy">
                  <div>
                    <input
                      type="checkbox"
                      id="agree"
                      onChange={(e) => {
                        handleAgreeCheckBoxChange(e.target.checked, e.target.value);
                      }}
                    />
                    <label htmlFor="agree"> 개인정보 수집 및 이용에 동의합니다. </label>
                  </div>
                  <button
                    type="button"
                    className="privacy-toggle-btn"
                    id="toggleBtn"
                    onClick={handleTermOpen}
                  >
                    {isTermOpen ? '내용닫기' : '내용보기'}
                  </button>
                </div>
                <div>
                  {isTermOpen ? (
                    <div className="" id="contentWrapper">
                      <div className="privacy-content">
                        <div>
                          <strong>개인정보의 처리 목적</strong>
                          <p className="mt-8">
                            회사는 수집한 개인정보를 다음 목적을 위해 활용합니다.
                          </p>
                          <p className="ml-12">
                            - 고객 문의사항 답변 및 안내를 위한 고객의 정보 수집
                          </p>
                        </div>
                        <div>
                          <strong>개인정보 수집항목 및 수집 방법</strong>
                          <p className="mt-8">
                            회사는 고객문의에 활용하기 위한 수집항목과 수집방법은 아래와 같습니다.
                          </p>
                          <p className="ml-12">
                            가. 수집항목 : 고객사명, 이메일, 연락처
                            <br />
                            나. 수집방법 : 홈페이지
                          </p>
                        </div>
                        <div>
                          <strong>개인정보의 처리 및 보유기간</strong>
                          <p className="mt-8">
                            고객문의에 활용하기 위해 수집된 개인정보의 처리 및 보유기간은 아래와
                            같습니다.
                          </p>
                          <p className="ml-12">
                            - 보존항목 : 고객 문의사항 답변 및 안내를 위한 고객의 정보
                            <br />- 보존기간 : 수집 후 5년
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
                <button className="btn-submit fs-base fw-bold" onClick={handleSubmit(onSubmit)}>
                  문의하기
                </button>
              </div>
            </div>
          </div>
        </main>
      </FormProvider>
    </>
  );
}

export default Contact;
